import { takeEvery, select, put } from 'redux-saga/effects';
import {
  revalidateImportRows,
  setImportRows,
  setImportGeoValidationResult,
  setImportDupCheckErrors,
  appNotify
} from 'store/actions';
import { importTypeStateSelector, opportunityImportSelectedChannelSelector, postAcqTaskImportChannelSelector, postAcqTaskImportRequestTypeSelector, postAcquisitionTaxonomySelector } from 'store/selectors';
import { isAcquisitionsChannel } from 'components/underwrite/postAcquisition/import/importTasks/taxonomyHelpers';
import { ValidateRows } from 'services/importValidationService';
import { Constants } from 'utilities/constants';

const geoFields = ['street', 'city', 'state', 'zipCode'];
const postAcqFields = ['assetId'];
// Acquisitions (address-only) rows: reset geo validation on an edit to the address inputs OR to the
// resolved outputs themselves (e.g. the user manually clears PM ID/lat/long expecting Validate Geo
// to be re-runnable) — not just the address.
const acquisitionsGeoFields = [...geoFields, 'asgPropID', 'latitude', 'longitude'];

export function* revalidateImportRowsSaga() {
  yield takeEvery(revalidateImportRows, function* ({ payload }) {
    const { importType, rows, isFromRejectedImports, skipGeoValidationReset } = payload;
    try {
      const importSelectedChannel = importType === Constants.ImportType.OPPORTUNITIES ? yield select(opportunityImportSelectedChannelSelector) : null;
      const postAcqChannel = importType === Constants.ImportType.POSTACQTASKS ? yield select(postAcqTaskImportChannelSelector) : null;
      const postAcqRequestType = importType === Constants.ImportType.POSTACQTASKS ? yield select(postAcqTaskImportRequestTypeSelector) : null;
      const postAcqTaxonomy = importType === Constants.ImportType.POSTACQTASKS ? yield select(postAcquisitionTaxonomySelector) : null;
      const importSource = importType === Constants.ImportType.POSTACQTASKS ? 'ReVUe' : Constants.ImportSource.ManualImport;
      const validatedRows = yield ValidateRows(rows, importSelectedChannel, postAcqChannel, postAcqRequestType, importType, importSource);
      if (!validatedRows) return;

      const actions = [setImportRows({ importType, importRows: validatedRows })];
      if (importType !== Constants.ImportType.TITLES) {
        let fieldChanged = false;
        const importTypeState = yield select(importTypeStateSelector, importType);
        const oldRows = importTypeState.importRows;
        // check if relevant fields changed by comparing old fields with new fields.
        // Acquisitions (address-only) Post-Acq imports resolve geo from the address, so an address
        // edit invalidates the resolved PM ID/lat-long — compare geo fields for that channel.
        const isAcquisitionsPostAcq = importType === Constants.ImportType.POSTACQTASKS && isAcquisitionsChannel(postAcqTaxonomy?.entries, postAcqChannel);
        const comparisonFields = importType === Constants.ImportType.POSTACQTASKS
          ? (isAcquisitionsPostAcq ? acquisitionsGeoFields : postAcqFields)
          : geoFields;
        for (let row of rows) {
          const foundOldRow = oldRows.find(old => old.referenceId === row.referenceId);
          if (foundOldRow) {
            for (let field of comparisonFields) {
              if (row[field] !== foundOldRow[field]) {
                fieldChanged = true;
                break;
              }
            }
          }
        }
        if (fieldChanged || isFromRejectedImports) { // Disable this if we want to reset geo validation on any field edits, instead of only on geo fields
          // Reset geo validation for geo-driven imports (all non-Post-Acq, plus the Acquisitions Post-Acq channel).
          // Skipped when the caller (e.g. the Location Verification modal) already applied an accurate,
          // row-scoped update to geoValidationResult itself — a blanket reset here would otherwise wipe out
          // every other row's geoValidationDto too, disabling their "Verify Location" context menu entry
          // until the user reruns Validate Geo.
          if (!skipGeoValidationReset && (importType !== Constants.ImportType.POSTACQTASKS || isAcquisitionsPostAcq)) {
            actions.push(setImportGeoValidationResult({
              importType,
              importGeoValidationResult: { passedValidation: null, invalidZipCodes: null, invalidCounties: null, invalidGeo: null, geoValidationDtos: null }
            }));
          }
          actions.push(setImportDupCheckErrors({
            importType,
            dupCheckErrors: []
          }));
        }
      }
      yield put(actions);
    }
    catch (error) {
      yield put(appNotify(error.message, 'error'));
      console.log('revalidateImportRowsSaga', error.message);
    }
  });
}